import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { imgrepeat } from "../../image";
import {
  AXIOS_GET,
  AXIOS_POST,
} from "../../server/api/crud";
import {
  getKlacht,
  getUser,
  setAccesToken,
  setAdmins,
  setAfspraken,
  setKlacht,
  setStates,
  setUser,
  setVoedingID,
  setVooruitgang,
} from "../../Localstorage/localstorage";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import {
  setAdminsLoad,
  setGebruikersLoad,
  setPatientenLoad,
} from "../../redux/userSlice";
import { AuthContext } from "../../context/AuthContext";
import Spinner2 from "../../Componenten/Spinner2";
import {
  setAppointmentsCalendlyLoad,
} from "../../redux/appointmentSlice";
import moment from "moment";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { parseCalendlyInfo } from "../../function";

const onderhoud = 0;

const Login = () => {
  const [progress, setProgress] = useState(0);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useContext(AuthContext); // Get the login function from AuthContext
  const [showPassword, setShowPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  const updateProgress = useCallback((nextValue) => {
    setProgress((prev) => (nextValue > prev ? nextValue : prev));
  }, []);
  const withTimeout = useCallback((promise, ms = 12000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms)
      ),
    ]);
  }, []);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const [isloading, setIsLoading] = useState(false);

  useEffect(() => {
    const user = getUser();
    const klacht = getKlacht();
    const role = user?.role != null ? Number(user.role) : NaN;
    if ((role === 2 && klacht) || role === 1) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDarkMode(root.classList.contains("dark"));
    });

    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const loginLogo = isDarkMode ? imgrepeat.logo_white : imgrepeat.logo_green;

  async function fetchAfspraken(userdetails) {
    try {
      const details = parseCalendlyInfo(userdetails?.calendly_info);
      const calendlyUserUri = details?.uri || "";
      if (!calendlyUserUri) {
        dispatch(setAppointmentsCalendlyLoad([]));
        setAfspraken([]);
        return;
      }

      // Fetch calendar events

      const queryParams = new URLSearchParams({
        user: calendlyUserUri,
        organization:
          "https://api.calendly.com/organizations/e5ba7299-d262-4435-acc9-67a68893e28f",
        sort: "start_time:asc",
        count: "100",
        min_start_time: moment().startOf("day").toISOString(),
        status: "active",
      }).toString();

      const [response, patientsResponse] = await Promise.all([
        AXIOS_GET(`calendly/scheduled?${queryParams}`),
        AXIOS_GET("users/all/patienten/not"),
      ]);

      // Filter events to only include future appointments and those from yesterday onwards
      const futureAppointments = response?.data?.collection || [];
      const patientRows = patientsResponse?.data?.data;
      const existingPatients = Array.isArray(patientRows)
        ? patientRows.filter(
            (user) =>
              user?.username !== "test2" &&
              user?.username !== "test3" &&
              user?.role_id === 2
          )
        : [];
      const existingEmails = new Set(
        existingPatients
          .flatMap((patient) => [patient?.email, patient?.username])
          .filter(Boolean)
          .map((value) => value.trim().toLowerCase())
      );

      //console.log('existingPatients ==> ', existingPatients);

      // Find appointments where the invitee is not an existing patient
      const seenNewInvitees = new Set();
      const unregisteredAppointments = futureAppointments.filter((event) => {
        const inviteeEmail = event?.invitees?.[0]?.email?.trim().toLowerCase();
        if (!inviteeEmail) return false;
        if (existingEmails.has(inviteeEmail)) return false;
        if (seenNewInvitees.has(inviteeEmail)) return false;
        seenNewInvitees.add(inviteeEmail);
        return true;
      });

      dispatch(setAppointmentsCalendlyLoad(unregisteredAppointments));
      setAfspraken(unregisteredAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  }

  const handleSignIn = async () => {
    setIsLoading(true);
    updateProgress(8);
    let signInSucceeded = false;

    // Validation
    const validateInputs = () => {
      if (!username || username.trim() === "") {
        alert("Gebruikersnaam mag niet leeg zijn");
        return false;
      }
      if (!password || password.trim() === "") {
        alert("Wachtwoord mag niet leeg zijn");
        return false;
      }
      if (password.length < 3) {
        alert("Wachtwoord moet minimaal 3 karakters lang zijn");
        return false;
      }
      return true;
    };

    if (!validateInputs()) {
      setIsLoading(false);
      return;
    }

    try {
      // Login Request
      const loginResponse = await AXIOS_POST("auth/login", {
        username,
        password,
      });
      const data = loginResponse?.data;

      if (!data?.accessToken) {
        throw new Error("Invalid login credentials");
      }

      // Store access token
      setAccesToken(data.accessToken);
      login(data.accessToken);
      setUser(data.user);
      updateProgress(24);

      const userRole =
        data?.user?.role != null ? Number(data.user.role) : NaN;
      const bootstrapTasks = [];
      let userdetail = null;

      if (userRole === 1) {
        try {
          const responseUser = await withTimeout(
            AXIOS_GET(`users/${data.user.id}`),
            10000
          );
          userdetail = responseUser?.data?.data?.[0] || null;
          if (userdetail) {
            bootstrapTasks.push(withTimeout(fetchAfspraken(userdetail), 12000));
          }
        } catch (error) {
          console.warn("Skipping afspraken preload:", error?.message || error);
        }
      }
      updateProgress(42);

      // Fetch role-based data (timeouts prevent loader from being stuck)
      if (userRole === 2) {
        bootstrapTasks.push(withTimeout(fetchAdminData(data.user.id), 12000));
        bootstrapTasks.push(withTimeout(fetchComplaintData(data.user.id), 12000));
      } else if (userRole === 1) {
        bootstrapTasks.push(withTimeout(fetchPatientData(), 12000));
        bootstrapTasks.push(withTimeout(fetchAdminDataHoofd(), 12000));
      }
      updateProgress(70);

      if (bootstrapTasks.length > 0) {
        await Promise.allSettled(bootstrapTasks);
      }
      updateProgress(95);

      // Fetch Complaint Data

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: `Welkom ${data.user.username}`,
        showConfirmButton: false,
        timer: 2500,
      });
      updateProgress(100);
      signInSucceeded = true;
    } catch (error) {
      console.error("Error during sign-in:", error);
      const status = error?.response?.status;
      const isCredentialError = status === 401 || status === 403;

      Swal.fire({
        position: "center",
        icon: "error",
        title: isCredentialError
          ? "Inloggen mislukt. Controleer uw gebruikersnaam en wachtwoord."
          : "Inloggen tijdelijk niet beschikbaar. Probeer opnieuw.",
        showConfirmButton: false,
        timer: 2500,
      });
    } finally {
      // Keep loader mounted after success so Spinner2 can visually reach 100%
      // and trigger route change via onComplete.
      if (!signInSucceeded) {
        setIsLoading(false);
      }
    }
  };

  // Helper Functions
  const fetchAdminData = async (userId) => {
    try {
      const [adminsRes, voedingRes, vooruitgangRes] = await Promise.all([
        AXIOS_GET("users/all/admins"),
        AXIOS_GET(`voedsel/${userId}`),
        AXIOS_GET(`vooruitgang/user/${userId}`),
      ]);

      const adminsPayload = adminsRes?.data?.data;
      if (Array.isArray(adminsPayload)) {
        dispatch(setAdminsLoad(adminsPayload));
        setAdmins(adminsPayload);
      }

      const voedingRows = voedingRes?.data?.data;
      const vooruitgangRows = vooruitgangRes?.data?.data;
      if (Array.isArray(voedingRows) && voedingRows[0] != null) {
        setVoedingID(voedingRows[0]);
      }
      if (Array.isArray(vooruitgangRows) && vooruitgangRows[0] != null) {
        setVooruitgang(vooruitgangRows[0]);
      }

      updateProgress(72);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
  };

  const fetchAdminDataHoofd = async () => {
    try {
      const adminsRes = await AXIOS_GET("users/all/admins");
      const rawAdmins = adminsRes?.data?.data;
      const admins = Array.isArray(rawAdmins)
        ? rawAdmins.filter(
            (item) => item.user_id != "93d14850-bab4-de7d-4c8e-dd1cf3443891"
          )
        : [];

      dispatch(setAdminsLoad(admins));
      setAdmins(admins);

      updateProgress(76);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
  };

  const fetchPatientData = async () => {
    try {
      const [patientsRes, usersRes] = await Promise.all([
        AXIOS_GET("users/all/patienten"),
        AXIOS_GET("users/all"),
      ]);

      const patienten = patientsRes?.data?.data;
      const gebruikers = usersRes?.data?.data;
      if (Array.isArray(patienten)) {
        dispatch(setPatientenLoad(patienten));
      }
      if (Array.isArray(gebruikers)) {
        dispatch(setGebruikersLoad(gebruikers));
      }

      updateProgress(70);
    } catch (error) {
      console.error("Error fetching patient data:", error);
    }
  };

  const fetchComplaintData = async (userId) => {
    try {
      const complaintRes = await AXIOS_GET(`klachtenregistratie/${userId}`);
      const raw = complaintRes?.data;
      const complaints = Array.isArray(raw)
        ? raw
        : raw && Array.isArray(raw.data)
          ? raw.data
          : [];

      if (complaints.length > 0) {
        setKlacht(complaints[0]);
        setStates({ id: complaints[0]?.status });
      } else {
        setKlacht(null);
        setStates({ id: 0 });
      }
    } catch (error) {
      console.error("Error fetching complaint data:", error);
      // Complaint lookup should not block login flow.
      setKlacht(null);
      setStates({ id: 0 });
    }
  };

  if (isloading)
    return (
      <Spinner2
        progress={progress}
        onComplete={() => {
          setIsLoading(false);
          navigate("/dashboard");
        }}
      />
    );

  if (onderhoud === 0)
    return (
      <div className="auth-page-bg flex h-screen w-full flex-col items-center justify-center px-4 text-wa-text lg:px-8">
        <div className="auth-bg-orb auth-orb-one" />
        <div className="auth-bg-orb auth-orb-two" />
        <div className="auth-bg-orb auth-orb-three" />
        <div className="auth-page-content flex h-full w-full flex-col items-center justify-center">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-20 w-auto"
            src={loginLogo}
            alt="RepeatHealth"
          />
          <h1 className="mt-10 text-center text-4xl font-bold leading-9 tracking-tight text-wa-text">
            Repeatconnect.
          </h1>
          <h2 className="mt-2 text-center text-lg font-bold leading-9 tracking-tight text-wa-muted">
            Gelieve u hieronder in te loggen.
          </h2>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <div className="space-y-6">
            <div>
              <div className="mt-2">
                <input
                  id="username"
                  name="username"
                  type="username"
                  placeholder="Gebruikersnaam"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="block w-full text-lg rounded-md border-0 px-4 py-4 text-gray-900 dark:text-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-darkblue-600 dark:focus:ring-green-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Nieuwe wachtwoord"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-4 text-gray-900 dark:text-white dark:bg-gray-800 rounded-md shadow-sm ring-1 ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-darkblue-600 dark:focus:ring-green-500"
                  />
                  <button
                    type="button"
                    onClick={handleTogglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-600 dark:text-gray-400 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end mt-4">
                <div className="text-sm">
                  <a
                    href="mailto:info@repeathealth.com"
                    className="font-semibold text-green-800 dark:text-green-400 hover:text-green-400 dark:hover:text-green-300"
                  >
                    Wachtwoord vergeten?
                  </a>
                </div>
              </div>
            </div>
            <div>
              <button
                onClick={handleSignIn}
                className="flex w-full justify-center rounded-md bg-green-800 px-4 py-4 text-sm font-semibold leading-6 text-green-100 shadow-sm transition-colors hover:bg-white/50 hover:text-green-800 dark:bg-white dark:text-green-700 dark:hover:bg-white/50 dark:hover:text-green-800 dark:focus-visible:outline-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
              >
                Inloggen
              </button>
              <div className="mt-4 justify-center flex">
                <p className="text-gray-900 dark:text-gray-300">
                  Heb je nog geen account?{" "}
                  <a
                    href="/register"
                    className="font-semibold hover:text-green-600 dark:hover:text-green-400 text-green-800 dark:text-green-400"
                  >
                    meld je aan.
                  </a>
                </p>
              </div>
            </div>
          </div>
          {/* <div className="relative mt-4">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white dark:bg-gray-800 px-2 text-sm text-gray-500 dark:text-gray-400">Of</span>
            </div>
          </div>
          <div className='mt-4'>
          <div className="px-6 sm:px-0 max-w-sm ">
              <button type="button" 
              className="text-white text-lg px-4 py-4 w-full  bg-[#4285F4] hover:bg-red-600 focus:ring-4 focus:outline-none focus:ring-[#4285F4]/50 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center justify-center mr-2 mb-2">
                <svg className="mr-2 -ml-1 w-6 h-6" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                  <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                </svg>
                <span className='flex '>
        
                Log u in met Google
                </span>

              </button>
          </div>
          </div> */}
        </div>
        </div>
      </div>
    );

  if (onderhoud === 1)
    return (
      <div className="auth-page-bg flex h-screen w-full flex-col items-center justify-center px-4 text-wa-text lg:px-8">
        <div className="auth-bg-orb auth-orb-one" />
        <div className="auth-bg-orb auth-orb-two" />
        <div className="auth-bg-orb auth-orb-three" />
        <div className="auth-page-content flex h-full w-full flex-col items-center justify-center">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-20 w-auto"
            src={loginLogo}
            alt="RepeatHealth"
          />
          <h1 className="mt-10 text-center text-4xl font-bold leading-9 tracking-tight text-wa-text">
            Repeatconnect.
          </h1>
        </div>
        <div className="mt-16 rounded-xl border border-wa-border bg-wa-panel2 p-4">
          <p className="text-sm text-wa-muted">
          ⚠ Website tijdelijk niet beschikbaar<br/>
          Onze website is wegens onderhoud van 15 april tot en met 17 april niet bereikbaar.
          <br/>
          Voor vragen of afspraken kunt u ons bereiken via e-mail: <a className="rounded bg-wa-hover p-1 text-wa-accent underline" href="mailto:info@repeathealth.com">info@repeathealth.com</a>.<br/><br/>

          Bedankt voor uw begrip.
          </p>
        </div>
        </div>
      </div>
    );

  if (onderhoud === 2)
    return (
      <div className="auth-page-bg flex h-screen w-full flex-col items-center justify-center px-4 text-wa-text lg:px-8">
        <div className="auth-bg-orb auth-orb-one" />
        <div className="auth-bg-orb auth-orb-two" />
        <div className="auth-bg-orb auth-orb-three" />
        <div className="auth-page-content flex h-full w-full flex-col items-center justify-center">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-20 w-auto"
            src={loginLogo}
            alt="RepeatHealth"
          />
          <h1 className="mt-10 text-center text-4xl font-bold leading-9 tracking-tight text-wa-text">
            Repeatconnect.
          </h1>
          <h2 className="mt-2 text-center text-lg font-bold leading-9 tracking-tight text-wa-muted">
            Gelieve u hieronder in te loggen.
          </h2>
        </div>
        <div className="rounded-lg border border-orange-500 dark:border-orange-600 p-4 bg-orange-100 dark:bg-orange-900/30 mt-4">
          <p className="text-orange-700 dark:text-orange-300">
            Beste Repeatconnect-gebruiker,
            <br />
            Op 15 en 16 augustus 2025 is onze website in onderhoud.
            <br />
            De website zal in deze periode mogelijk niet beschikbaar zijn.{" "}
            <br />
            Onze excuses voor het ongemak.
          </p>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <div className="space-y-6">
            <div>
              <div className="mt-2">
                <input
                  id="username"
                  name="username"
                  type="username"
                  placeholder="Gebruikersnaam"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="block w-full text-lg rounded-md border-0 px-4 py-4 text-gray-900 dark:text-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-darkblue-600 dark:focus:ring-green-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Nieuwe wachtwoord"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-4 text-gray-900 dark:text-white dark:bg-gray-800 rounded-md shadow-sm ring-1 ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-darkblue-600 dark:focus:ring-green-500"
                  />
                  <button
                    type="button"
                    onClick={handleTogglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-600 dark:text-gray-400 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end mt-4">
                <div className="text-sm">
                  <a
                    href="mailto:info@repeathealth.com"
                    className="font-semibold text-green-800 dark:text-green-400 hover:text-green-400 dark:hover:text-green-300"
                  >
                    Wachtwoord vergeten?
                  </a>
                </div>
              </div>
            </div>
            <div>
              <button
                onClick={handleSignIn}
                className=""
              >
                Inloggen
              </button>
              <div className="mt-4 justify-center flex">
                <p className="text-gray-900 dark:text-gray-300">
                  Heb je nog geen account?{" "}
                  <a
                    href="/register"
                    className="font-semibold hover:text-green-600 dark:hover:text-green-400 text-green-800 dark:text-green-400"
                  >
                    meld je aan.
                  </a>
                </p>
              </div>
            </div>
          </div>
          {/* <div className="relative mt-4">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-2 text-sm text-gray-500">Of</span>
                </div>
              </div>
              <div className='mt-4'>
              <div className="px-6 sm:px-0 max-w-sm ">
                  <button type="button" 
                  className="text-white text-lg px-4 py-4 w-full  bg-[#4285F4] hover:bg-red-600 focus:ring-4 focus:outline-none focus:ring-[#4285F4]/50 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center justify-center mr-2 mb-2">
                    <svg className="mr-2 -ml-1 w-6 h-6" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                      <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                    </svg>
                    <span className='flex '>
            
                    Log u in met Google
                    </span>
    
                  </button>
              </div>
              </div> */}
        </div>
        </div>
      </div>
    );
};

export default Login;
